import { Trans, useLingui } from "@lingui/react/macro";
import { FareCode } from "@silversea-ssc/web-fares";
import React, { useCallback } from "react";
import { CruisePriceAndOffersV2Response } from "src/ssc-api/prices/useCruisePrice";
import {
  Button,
  FareCardBase,
  FareCardBaseContent,
  FareCardFooter,
  FareCardHeader,
  FareCardHeaderProps,
  FareCardRefundability,
  PriceItem,
  PromoBadgeContent
} from "@silversea-ssc/ui-components-react";
import { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups";
import { isPresent } from "src/utils/checks";
import { computeFareCardInfo } from "features/fare/infographic/FareCard/utils";
import FareCardInfographic, {
  FareCardInfographicProps
} from "features/fare/infographic/FareCard/FareCardInfographic";
import { useFarePenaltiesModal } from "features/cancellation-policy/FarePenaltiesModalProvider";
import {
  isPriceWaitlist,
  perGuestLabel,
  transformPriceFrom
} from "components/Prices/utils";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { NewEcommerceButton } from "components/ECommerceButtons";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import FareCardDisclaimer from "features/fare/infographic/FareCard/FareCardDisclaimer";
import * as styles from "./CruiseFareCard.module.scss";
import { useExpandableFareCard } from "../ExpandableFareCardProvider";
import { tryAppendFareParam } from "features/fare/infographic/CruiseFares/append-fare-param";
import { useStaticFareLabels } from "features/fare/use-static-fare-labels";

export type CruiseFareCardProps = {
  title: string | undefined;
  headline: string | undefined;
  description: string | undefined;
  fareCode: FareCode;
  cruiseGroup: CruiseGroupCode | undefined;
  cruisePrices: CruisePriceAndOffersV2Response;
  ecommerceStatus?: ECommerceStatus;
  raqCTAUrl: string | undefined;
  activePromos: PromoBadgeContent[];
  onPromoBadgeClicked?: FareCardHeaderProps["onPromoBadgeClicked"];
  variant: FareCardInfographicProps["variant"];
};

const CruiseFareCard: React.FC<CruiseFareCardProps> = ({
  title,
  description,
  headline,
  fareCode,
  cruiseGroup,
  cruisePrices,
  ecommerceStatus,
  raqCTAUrl,
  activePromos,
  onPromoBadgeClicked,
  variant
}) => {
  const { t } = useLingui();
  const geolocation = useGeolocation();
  const { fareCodeLabel } = useStaticFareLabels();
  const { onRequestOpenPenaltiesModal, remotePaymentTermByFareCode } =
    useFarePenaltiesModal();
  const paymentTerms = remotePaymentTermByFareCode(fareCode);
  const { isExpanded, onToggleFareCardExpandClick } = useExpandableFareCard();
  const isExpandable = variant == "default";

  const onPenaltiesModalClick = useCallback(() => {
    onRequestOpenPenaltiesModal({
      fareCode
    });
  }, [fareCode, onRequestOpenPenaltiesModal]);

  const fareInfo = cruisePrices.fares.find((f) => f.fare == fareCode);
  if (!isPresent(fareInfo)) return null;

  const { inclusionItems, priceFrom, features } = fareInfo;
  const price = transformPriceFrom(
    t,
    priceFrom,
    geolocation.defaultCurrency,
    true,
    "only-from",
    fareCodeLabel
  );
  const isWaitlist = isPriceWaitlist(priceFrom);

  const {
    cancellationPolicyButtonLabel,
    depositRefundabilityContent,
    refundCreditExchangeFooterNotes,
    isRefundable
  } = computeFareCardInfo({
    t,
    cruiseGroup,
    paymentTerms: paymentTerms
  });

  return (
    <FareCardBase trackingName={`infographic_${fareCode}`}>
      <FareCardHeader
        title={title || ""}
        subtitle={headline}
        description={description}
        withPromos
        promos={activePromos}
        onPromoBadgeClicked={onPromoBadgeClicked}
        backgroundColor="neutral01"
      >
        <FareCardRefundability
          isRefundable={isRefundable}
          content={depositRefundabilityContent}
        />
        <div>
          <PriceItem
            chipBackgroundColor="white"
            price={price}
            variant="big"
            layout="row"
            showFareLabel={true}
            priceType="primary"
          />

          <div className={styles.perGuestsLabel}>
            {isWaitlist ? " " : t(perGuestLabel())}
          </div>
        </div>
        <NewEcommerceButton
          raqUrl={raqCTAUrl}
          ecommerceStatus={tryAppendFareParam(
            ecommerceStatus,
            fareCode,
            features
          )}
          variant="primary"
          size="regular"
          overrideEcommerceLabel={
            <Trans id="ecommerce-fare-button-label">Book with this fare</Trans>
          }
        />
      </FareCardHeader>
      <FareCardBaseContent>
        <FareCardInfographic
          expandable
          isExpanded={isExpanded}
          cruiseGroup={cruiseGroup}
          enabledInclusions={inclusionItems.map((x) => x.code)}
          fareCode={fareCode}
          inclusionsContentTokens={cruisePrices.tokens}
          variant={variant}
        />
      </FareCardBaseContent>

      <React.Fragment>
        {isExpanded || !isExpandable ? (
          <FareCardFooter withSeparator>
            <FareCardDisclaimer content={refundCreditExchangeFooterNotes} />
            <Button
              renderAs="button"
              variant="inline-text"
              onClick={onPenaltiesModalClick}
              dataContentPiece="read_our_cancellation_policy"
              noWrap
            >
              {cancellationPolicyButtonLabel}
            </Button>
          </FareCardFooter>
        ) : (
          <FareCardFooter>
            <Button
              renderAs="button"
              variant="text"
              onClick={onToggleFareCardExpandClick}
              dataContentPiece="expand_fare_card"
              iconName="chevron-down"
              noWrap
              full
            >
              <Trans>View more info</Trans>
            </Button>
          </FareCardFooter>
        )}
      </React.Fragment>
    </FareCardBase>
  );
};

export default CruiseFareCard;
